import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react'
import { cn } from './cn'

const inputClass =
  'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg ' +
  'placeholder:text-fg-disabled ' +
  'focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

type FieldShellProps = {
  label?: string
  hint?: string
  error?: string
  required?: boolean
  htmlFor?: string
  children: ReactNode
  className?: string
}

export function FieldShell({ label, hint, error, required, htmlFor, children, className }: FieldShellProps) {
  return (
    <div className={cn('block', className)}>
      {label && (
        <label htmlFor={htmlFor} className="block text-xs font-medium text-fg-muted mb-1.5">
          {label}
          {required && <span className="text-danger ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
      {!error && hint && <p className="mt-1.5 text-xs text-fg-subtle">{hint}</p>}
    </div>
  )
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & Omit<FieldShellProps, 'children'>

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, className, id, ...rest },
  ref
) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={id}>
      <input
        ref={ref}
        id={id}
        required={required}
        className={cn(inputClass, error && 'border-danger focus:ring-danger/30 focus:border-danger', className)}
        {...rest}
      />
    </FieldShell>
  )
})

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & Omit<FieldShellProps, 'children'>

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, className, id, rows = 4, ...rest },
  ref
) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={id}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        className={cn(inputClass, 'resize-y', error && 'border-danger', className)}
        {...rest}
      />
    </FieldShell>
  )
})
